import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Card,
  CardTitle,
  dropOffPointViewModes,
  returnStatuses,
  ShippingDetails,
  ToastContext,
} from '@reveni-io/reveni-ui'

import OpenClaimModal from '@/common/components/OpenClaimModal/OpenClaimModal'
import { userPermissions } from '@/common/constants'
import useOpenClaim from '@/common/hooks/useOpenClaim/useOpenClaim'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import { statuses } from '@/returns/constants'
import returnsServices from '@/returns/services/services'

import { actionsReasons } from '../constants'
import GenerateLabelModal from './GenerateLabelModal/GenerateLabelModal'
import ReturnShippingMenu from './ReturnShippingMenu/ReturnShippingMenu'
import ShippingDetailsCardSkeleton from './ShippingDetailsCardSkeleton/ShippingDetailsCardSkeleton'
import ShippingTrackingSection from './ShippingTrackingSection/ShippingTrackingSection'
import { getTrackingNumbers } from './helpers'

const ShippingDetailsCard = ({
  loading,
  data,
  sendTrackingNumber,
  fetchData,
  fetchTimeline,
  onSchedulePickup,
  onEditAddress,
  onAddTrackingNumber,
  onSendInstructions,
  onChangeDeliveryMethod,
  onGenerateLabel,
  generatingLabel,
  isSchedulePickupDisabled,
  showShippingMenu = true,
  isDisabledForOnHoldAndPendingStatus,
}) => {
  const { t } = useTranslation()
  const { hasPermission } = useUserPermissions()
  const [showGenerateLabelModal, setShowGenerateLabelModal] = useState(false)
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState(null)
  const { setToastInfo } = useContext(ToastContext)

  const { showOpenClaimModal, openOpenClaimModal, closeOpenClaimModal, onOpenClaim, openingClaim, uploadClaimImage } =
    useOpenClaim({
      storeId: data?.store_id,
      labelId: selectedTrackingNumber?.id,
      fetchData: fetchData,
      fetchTimeline,
      setToastInfo,
      openClaimService: returnsServices.openClaim,
      uploadImageService: returnsServices.uploadProductImage,
      resourceId: data?.id,
    })

  const trackingNumbers = getTrackingNumbers({
    customerTrackingNumber: data?.customer_shipment?.customer_tracking_number,
    merchantTrackingNumber: data?.shipping?.merchant_tracking_numbers,
    returnLabels: data?.shipping?.return_labels,
  })

  const handleGenerateLabel = async ({ includeRejectedItems, discardLabels }) => {
    try {
      await onGenerateLabel({ includeRejectedItems, discardLabels })
      setShowGenerateLabelModal(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleOpenClaimModal = trackingNumber => {
    setSelectedTrackingNumber(trackingNumber)
    openOpenClaimModal()
  }

  if (loading) return <ShippingDetailsCardSkeleton />

  return (
    <>
      <Card showBorder={false}>
        <CardTitle>
          {t('Envío devolución')}
          {showShippingMenu && (
            <ReturnShippingMenu
              allowsSchedulePickupReason={
                hasPermission(userPermissions.editReturnLabels, data?.store_id)
                  ? data?.permissions?.allows_schedule_pickup_reason
                  : actionsReasons.notSupported
              }
              allowsGenerateLabelReason={
                hasPermission(userPermissions.editReturnLabels, data?.store_id)
                  ? data?.permissions?.allows_generate_label_reason
                  : actionsReasons.notSupported
              }
              onSchedulePickup={onSchedulePickup}
              onGenerateLabel={() => setShowGenerateLabelModal(true)}
              pickupScheduled={data?.shipping?.pickup_scheduled}
              onEditAddress={onEditAddress}
              onAddTrackingNumber={onAddTrackingNumber}
              onSendInstructions={onSendInstructions}
              onChangeDeliveryMethod={onChangeDeliveryMethod}
              isSchedulePickupDisabled={isSchedulePickupDisabled}
              allowsEditReturnLabels={hasPermission(userPermissions.editReturnLabels, data?.store_id)}
              isDisabledForOnHoldAndPendingStatus={isDisabledForOnHoldAndPendingStatus}
              allowsMarkAsShipped={data?.permissions?.allows_mark_as_shipped}
              allowsReturnInstructionsRequest={data?.permissions?.allows_return_instructions_request}
              allowsUpdateDeliveryMethod={data?.permissions?.allows_update_delivery_method}
              allowsUpdateAddress={data?.permissions?.allows_update_address}
            />
          )}
        </CardTitle>
        <ShippingDetails
          deliveryMethod={data?.shipping?.delivery_method}
          dropOffPointViewMode={dropOffPointViewModes.separateFields}
          showTrackingNumber={
            hasPermission(userPermissions.editReturnStatus, data?.store_id) &&
            [statuses.inProgress, statuses.shipped].includes(data?.status)
          }
          onSave={sendTrackingNumber}
          trackingNumber={data?.shipping?.tracking_number}
          shippingAddress={data?.address}
          trackingUrl={data?.shipping?.delivery_method?.tracking_url}
          pickupScheduled={data?.shipping?.pickup_scheduled}
          eta={data?.status === returnStatuses.shipped ? data?.eta : null}
        />
        <ShippingTrackingSection
          parcels={data?.shipping?.parcels}
          storeId={data?.store_id}
          trackingNumbers={trackingNumbers}
          onOpenClaim={handleOpenClaimModal}
          orderTrackingPageUrl={data?.order?.order_tracking_page_url}
        />
      </Card>
      <GenerateLabelModal
        show={showGenerateLabelModal}
        onClose={() => setShowGenerateLabelModal(false)}
        loading={generatingLabel}
        onGenerateLabel={handleGenerateLabel}
        lineItems={data?.line_items}
        lineItemGroups={data?.line_item_groups}
        includesRejectedItems={data?.shipping?.includes_rejected_items}
        returnLabels={data?.shipping?.return_labels}
      />
      <OpenClaimModal
        show={showOpenClaimModal}
        onClose={closeOpenClaimModal}
        onOpenClaim={onOpenClaim}
        uploadImage={uploadClaimImage}
        loading={openingClaim}
      />
    </>
  )
}

export default ShippingDetailsCard
